/* global setInterval, clearInterval */
/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * useServerTimer.js
 *
 * The caller's ONE backend-held timer (contract C1 — a work-order segment or a
 * project entry) as a glanceable card with its verbs, for surfaces that show the
 * running clock without owning a work sheet (the service dashboard).
 *
 * Same contract as the work sheet ({@link module:helpers/useWorkOrderTimers}) and
 * the Time Tracker: the server stamps `startedAt`, the screen shows
 * `now - startedAt` from the wall clock, so locking the phone loses nothing. The
 * state is re-read on focus and when the app returns to the foreground.
 *
 * @module helpers/useServerTimer
 */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {AppState} from 'react-native';
import {useFocusEffect, useIsFocused} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';
import {ServiceModuleToast} from './ServiceModuleFeedback';
import {
    breakTracking,
    fetchTrackerState,
    pauseTracking,
    resumeTracking,
    resumeWorkFromBreak,
    stopTracking,
} from './timeTrackingApi';
import {resolveTrackerState} from './timeTrackerFormat';
import {useWorkday} from './WorkdayContext';
import refreshWidgets from './widgets/widgetRefresh';
import {buildServerTimerCard} from './workOrderTimerView';

/** Minimum gap between two background re-reads triggered by returning to the foreground. */
const REFRESH_MIN_GAP_MS = 4000;

/**
 * The caller's backend timer + its verbs.
 *
 * @returns {{card: (object|null), busy: boolean, pause: Function, resume: Function, startBreak: Function, stop: Function, refresh: Function}}
 *   `card` is null while nothing runs or is paused.
 */
export const useServerTimer = () => {
    const {t} = useTranslation();
    const {refresh: refreshWorkday} = useWorkday();
    const focused = useIsFocused();
    const [timer, setTimer] = useState(null);
    const [busy, setBusy] = useState(false);
    const [nowMs, setNowMs] = useState(() => Date.now());
    const mountedRef = useRef(true);
    const busyRef = useRef(false);
    const epochRef = useRef(0);
    const lastRefreshRef = useRef(0);
    const timerRef = useRef(timer);

    timerRef.current = timer;

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
        };
    }, []);

    const refresh = useCallback(async ({force = false} = {}) => {
        const startedAt = Date.now();

        if (busyRef.current || (!force && startedAt - lastRefreshRef.current < REFRESH_MIN_GAP_MS)) {
            return;
        }

        lastRefreshRef.current = startedAt;
        const epoch = epochRef.current;
        const state = await fetchTrackerState();

        // Fail-soft: a failed read keeps what is shown (it is NOT "nothing runs").
        if (state.ok && mountedRef.current && !busyRef.current && epoch === epochRef.current) {
            setTimer(state.timer);
            setNowMs(Date.now());
        }
    }, []);

    useFocusEffect(useCallback(() => {
        refresh({force: true});

        return undefined;
    }, [refresh]));

    useEffect(() => {
        const subscription = AppState.addEventListener('change', (next) => {
            if (next === 'active') {
                // The wall clock kept running while the phone was locked: repaint from it, then reconcile.
                setNowMs(Date.now());
                refresh();
            }
        });

        return () => subscription.remove();
    }, [refresh]);

    const state = resolveTrackerState(timer);
    const live = focused && (state === 'running' || state === 'break');

    useEffect(() => {
        if (!live) {
            return undefined;
        }

        setNowMs(Date.now());
        const handle = setInterval(() => setNowMs(Date.now()), 1000);

        return () => clearInterval(handle);
    }, [live]);

    const verb = useCallback(async (job, failureKey, failureText) => {
        if (busyRef.current) {
            return false;
        }

        busyRef.current = true;
        epochRef.current += 1;
        setBusy(true);

        try {
            const next = await job();

            if (mountedRef.current) {
                setTimer(next && next.id ? next : null);
                setNowMs(Date.now());
            }
            Promise.resolve(refreshWidgets()).catch(() => {});
            Promise.resolve(refreshWorkday()).catch(() => {});

            return true;
        } catch (error) {
            ServiceModuleToast.error(t(failureKey, {defaultValue: failureText}), error?.payload?.message || '');
            busyRef.current = false;
            await refresh({force: true});

            return false;
        } finally {
            busyRef.current = false;
            if (mountedRef.current) {
                setBusy(false);
            }
        }
    }, [refresh, refreshWorkday, t]);

    const pause = useCallback(() => verb(pauseTracking, 'tt_error_pause', 'Nem sikerült szüneteltetni.'), [verb]);
    const startBreak = useCallback(() => verb(breakTracking, 'tt_error_break', 'Nem sikerült elindítani a pihenőt.'), [verb]);
    const stop = useCallback(() => verb(stopTracking, 'tt_error_stop', 'Nem sikerült leállítani.'), [verb]);
    // A tracked BREAK is left by re-starting the same target; only a PAUSED row is reopened by `resume`.
    const resume = useCallback(() => verb(
        () => (resolveTrackerState(timerRef.current) === 'break' ? resumeWorkFromBreak(timerRef.current) : resumeTracking()),
        'tt_error_resume',
        'Nem sikerült folytatni.',
    ), [verb]);

    const card = useMemo(() => buildServerTimerCard(timer, nowMs, t), [timer, nowMs, t]);

    return useMemo(() => ({card, busy, pause, resume, startBreak, stop, refresh}), [card, busy, pause, resume, startBreak, stop, refresh]);
};

export default useServerTimer;
